import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ScanText } from "lucide-react";

import { api, ApiError } from "../../api";
import { Note, SettingsSection } from "./shared";

type DescriptionToggles = {
  people_description: boolean;
  activity_description: boolean;
};

function readToggles(raw: Record<string, unknown> | undefined): DescriptionToggles {
  const v =
    raw && typeof raw.vllm === "object" && raw.vllm !== null
      ? (raw.vllm as Record<string, unknown>)
      : {};
  return {
    people_description: v.people_description !== false,
    activity_description: v.activity_description !== false,
  };
}

function OnOff({
  label,
  description,
  value,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="bf-card space-y-3">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
          <ScanText size={15} />
          {label}
        </h3>
        <p className="mt-0.5 text-xs text-bf-muted">{description}</p>
      </div>
      <div role="radiogroup" aria-label={label} className="flex gap-2">
        {(
          [
            { id: true, text: "On" },
            { id: false, text: "Off" },
          ] as const
        ).map((opt) => {
          const selected = value === opt.id;
          return (
            <button
              key={String(opt.id)}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(opt.id)}
              className={
                "flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 " +
                (selected
                  ? "bg-brand-purple/15 text-white ring-1 ring-inset ring-brand-purple/40"
                  : "bg-white/[0.03] text-bf-muted ring-1 ring-inset ring-white/10 hover:bg-white/5 hover:text-bf-text")
              }
            >
              {opt.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DescriptionsSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: ["config"], queryFn: api.config.get });
  const [draft, setDraft] = useState<DescriptionToggles | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cfg.data) setDraft(readToggles(cfg.data));
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: (body: DescriptionToggles) =>
      api.config.patch({
        vllm: {
          people_description: body.people_description,
          activity_description: body.activity_description,
        },
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["config"] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not save descriptions."),
  });

  if (cfg.isLoading || !draft) {
    return (
      <SettingsSection title="Descriptions">
        <div className="bf-card text-sm text-bf-muted">Loading descriptions…</div>
      </SettingsSection>
    );
  }

  const current = readToggles(cfg.data);
  const dirty =
    draft.people_description !== current.people_description ||
    draft.activity_description !== current.activity_description;

  return (
    <SettingsSection
      title="Descriptions"
      description="Choose whether each person is described by how they look, by what they are doing, or both."
      hint="A camera can follow these settings or set its own on the Cameras page."
      actions={
        isAdmin && (
          <button
            type="button"
            className="bf-button disabled:opacity-40"
            disabled={!dirty || save.isPending}
            onClick={() => {
              setSaved(false);
              save.mutate(draft);
            }}
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}
      {saved && !save.isPending && <Note tone="success">Descriptions saved.</Note>}

      <div className="grid gap-3 lg:grid-cols-2">
        <OnOff
          label="People description"
          description="Clothing, hair and other appearance details."
          value={draft.people_description}
          disabled={!isAdmin}
          onChange={(people_description) => {
            setSaved(false);
            setDraft({ ...draft, people_description });
          }}
        />
        <OnOff
          label="Activity description"
          description="What the person is doing."
          value={draft.activity_description}
          disabled={!isAdmin}
          onChange={(activity_description) => {
            setSaved(false);
            setDraft({ ...draft, activity_description });
          }}
        />
      </div>

      {!isAdmin && (
        <Note tone="info">These are shown read-only — an administrator can change them.</Note>
      )}
    </SettingsSection>
  );
}
